import { useMemberLoanHistory } from "@/hooks/useLoans";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Loader, User, Phone, Mail, Wallet, CreditCard, CheckCircle2,
  AlertTriangle, DollarSign, Calendar, Download, Clock,
  TrendingUp, FileText, History, XCircle, Eye, Send,
} from "lucide-react";
import { format } from "date-fns";
import { formatCurrency, cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

interface MemberLoanProfileProps {
  memberId: number | string;
  onClose: () => void;
}

export default function MemberLoanProfile({ memberId, onClose }: MemberLoanProfileProps) {
  const navigate = useNavigate();
  const { data, isLoading, error, isError } = useMemberLoanHistory(memberId);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader className="h-8 w-8 animate-spin text-green-600 mb-3" />
        <p className="text-sm text-gray-500">Loading member profile...</p>
      </div>
    );
  }

  if (isError || !data) {
    const errMsg = (error as any)?.response?.data?.message || (error as any)?.message || "Failed to load member profile";
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="h-14 w-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
          <XCircle className="h-7 w-7 text-red-500" />
        </div>
        <h3 className="text-base font-semibold text-gray-800 mb-1">Unable to Load Profile</h3>
        <p className="text-sm text-gray-500 text-center max-w-xs mb-4">{errMsg}</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    );
  }

  const { member, summary, loans, payments, schedules } = data;

  const statusColors: Record<string, string> = {
    pending: "bg-amber-100 text-amber-700",
    pending_approval: "bg-amber-100 text-amber-700",
    approved: "bg-blue-100 text-blue-700",
    active: "bg-green-100 text-green-700",
    disbursed: "bg-green-100 text-green-700",
    completed: "bg-emerald-100 text-emerald-700",
    fully_repaid: "bg-emerald-100 text-emerald-700",
    rejected: "bg-red-100 text-red-700",
    defaulted: "bg-red-100 text-red-700",
    overdue: "bg-orange-100 text-orange-700",
  };

  const activeLoans = loans.filter((l: any) => ["active", "disbursed"].includes(l.status));
  const repaidLoans = loans.filter((l: any) => ["completed", "fully_repaid"].includes(l.status));
  const pendingLoans = loans.filter((l: any) => ["pending", "pending_approval", "approved_by_leaders", "approved"].includes(l.status));
  const overdueLoans = loans.filter((l: any) => l.status === "overdue");

  const overdueAmount = overdueLoans.reduce((sum: number, l: any) => sum + (Number(l.dueAmount) || 0), 0);

  return (
    <div className="space-y-5 max-h-[80vh] overflow-y-auto">
      {/* Member Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
            <User className="h-8 w-8 text-green-700" />
          </div>
          <div>
            <h2 className="text-xl font-bold">{member.name}</h2>
            <p className="text-sm text-gray-500">Member ID: {member.memberId || "N/A"}</p>
            <div className="flex items-center gap-3 mt-1">
              {member.phone && (
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <Phone className="h-3 w-3" /> {member.phone}
                </span>
              )}
              {member.email && (
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <Mail className="h-3 w-3" /> {member.email}
                </span>
              )}
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="h-4 w-4 text-green-600" />
              <span className="text-xs text-gray-500">Total Savings</span>
            </div>
            <p className="text-lg font-bold text-green-700">{formatCurrency(member.savingsBalance)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <CreditCard className="h-4 w-4 text-blue-600" />
              <span className="text-xs text-gray-500">Active Loans</span>
            </div>
            <p className="text-lg font-bold text-blue-700">{activeLoans.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-amber-600" />
              <span className="text-xs text-gray-500">Pending Loans</span>
            </div>
            <p className="text-lg font-bold text-amber-700">{pendingLoans.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="text-xs text-gray-500">Overdue Amount</span>
            </div>
            <p className="text-lg font-bold text-red-700">{formatCurrency(overdueAmount)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex gap-2 flex-wrap">
        {member.id && (
          <Button variant="outline" size="sm" onClick={() => { onClose(); navigate(`/members/${member.id}`); }}>
            <Eye className="h-3.5 w-3.5 mr-1.5" />
            View Member
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => { onClose(); navigate(`/loans/reminders`); }}>
          <Send className="h-3.5 w-3.5 mr-1.5" />
          Send Notification
        </Button>
      </div>

      {/* Loan Applications */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Loan History ({loans.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loans.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">No loan applications found</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {loans.map((loan: any) => (
                <div key={loan.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  onClick={() => { onClose(); navigate(`/loans/${loan.loanCode || loan.id}`); }}>
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-semibold">{loan.loanCode}</span>
                        <Badge className={cn("text-[10px]", statusColors[loan.status])}>
                          {loan.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span>{formatCurrency(loan.amount)}</span>
                        <span>{loan.termMonths} months</span>
                        <span>{loan.loanType || "Loan"}</span>
                        {loan.applicationDate && (
                          <span>{format(new Date(loan.applicationDate), "dd MMM yyyy")}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500">
                      Paid: {formatCurrency(loan.totalPaid || 0)} / {formatCurrency(loan.amount)}
                    </div>
                    <div className="text-xs text-orange-600 mt-0.5">
                      Outstanding: {formatCurrency(loan.dueAmount || 0)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active Loans Schedule */}
      {activeLoans.length > 0 && schedules.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Repayment Schedule
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {schedules.map((s: any) => (
                <div key={s.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                  <div className="flex items-center gap-3">
                    <span className="text-gray-500">#{s.installmentNumber}</span>
                    <span>{s.dueDate ? format(new Date(s.dueDate), "dd MMM yyyy") : "-"}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span>{formatCurrency(s.totalAmount)}</span>
                    <span className="text-green-600">Paid: {formatCurrency(s.paidAmount)}</span>
                    <Badge className={cn("text-[10px]", statusColors[s.status])}>{s.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Repayment History */}
      {payments.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <History className="h-4 w-4" />
              Repayment History ({payments.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {payments.slice(0, 20).map((p: any, i: number) => (
                <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <div>
                      <span className="font-medium">{formatCurrency(p.amount)}</span>
                      <span className="text-gray-500 ml-2">{p.loanCode}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-gray-500">
                      {p.paymentDate ? format(new Date(p.paymentDate), "dd MMM yyyy") : "-"}
                    </span>
                    <span className="text-xs text-gray-400 ml-2">{p.paymentMethod || "cash"}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
